// Onboarding Types
// These types define the data structures used throughout the onboarding flow

// ==========================================
// Business Vertical & Staffing Model Types
// ==========================================

export type BusinessVertical =
  | "hair"
  | "nail"
  | "barber"
  | "aesthetician"
  | "med_spa";

export type StaffingModel = "full_service_w2" | "booth_renter_1099" | "hybrid";

export const BUSINESS_VERTICAL_OPTIONS: {
  value: BusinessVertical;
  label: string;
}[] = [
  { value: "hair", label: "Hair Salon" },
  { value: "nail", label: "Nail Salon" },
  { value: "barber", label: "Barbershop" },
  { value: "aesthetician", label: "Aesthetician" },
  { value: "med_spa", label: "Med Spa" },
];

export const STAFFING_MODEL_OPTIONS: {
  value: StaffingModel;
  label: string;
}[] = [
  { value: "full_service_w2", label: "Full-Service (W2)" },
  { value: "booth_renter_1099", label: "Booth Renter (1099)" },
  { value: "hybrid", label: "Hybrid" },
];

const BUSINESS_VERTICAL_SLUG_SET = new Set<string>(
  BUSINESS_VERTICAL_OPTIONS.map((o) => o.value),
);

/**
 * Map saved answers / API text to canonical `BusinessVertical` slugs so chip
 * selection and staffing visibility stay in sync (e.g. "Med Spa" → `med_spa`).
 */
export function normalizeBusinessVerticalValue(
  raw: string | null | undefined,
): BusinessVertical | null {
  if (raw == null) return null;
  const v = raw.trim();
  if (!v) return null;
  if (BUSINESS_VERTICAL_SLUG_SET.has(v)) {
    return v as BusinessVertical;
  }
  const underscored = v.toLowerCase().replace(/\s+/g, "_");
  if (BUSINESS_VERTICAL_SLUG_SET.has(underscored)) {
    return underscored as BusinessVertical;
  }
  const fromLabel = BUSINESS_VERTICAL_OPTIONS.find(
    (o) => o.label.toLowerCase() === v.toLowerCase(),
  );
  return fromLabel ? fromLabel.value : null;
}

// ==========================================
// Stage Enums
// ==========================================

export type OnboardingStage =
  | "about_yourself"
  | "business_basics"
  | "about_your_business"
  | "business_hours"
  | "business_goals";

// Stage order for navigation
export const STAGE_ORDER: OnboardingStage[] = [
  "about_yourself",
  "business_basics",
  "about_your_business",
  "business_hours",
  "business_goals",
];

// Get stage index (1-based for display)
export function getStageNumber(stage: OnboardingStage): number {
  return STAGE_ORDER.indexOf(stage) + 1;
}

// Get next stage
export function getNextStage(stage: OnboardingStage): OnboardingStage | null {
  const index = STAGE_ORDER.indexOf(stage);
  return index < STAGE_ORDER.length - 1 ? STAGE_ORDER[index + 1] : null;
}

// Get previous stage
export function getPreviousStage(
  stage: OnboardingStage,
): OnboardingStage | null {
  const index = STAGE_ORDER.indexOf(stage);
  return index > 0 ? STAGE_ORDER[index - 1] : null;
}

// Runtime validation for OnboardingStage
export function isValidOnboardingStage(
  stage: unknown,
): stage is OnboardingStage {
  return (
    typeof stage === "string" && STAGE_ORDER.includes(stage as OnboardingStage)
  );
}

// Safe getter for OnboardingStage with fallback to first stage
export function getValidOnboardingStage(
  stage: unknown,
  fallback: OnboardingStage = STAGE_ORDER[0],
): OnboardingStage {
  return isValidOnboardingStage(stage) ? stage : fallback;
}

// ==========================================
// Question Keys (used for API calls)
// ==========================================

// Stage 1: about_yourself
export const QUESTION_KEYS = {
  // Stage 1: about_yourself
  WHATS_YOUR_TITLE: "whats_your_title",
  HOW_DID_YOU_HEAR_ABOUT_US: "how_did_you_hear_about_us",

  // Stage 2: business_basics
  WHAT_TYPE_OF_BUSINESS: "what_type_of_business",
  WHAT_IS_YOUR_BUSINESS_VERTICAL: "what_is_your_business_vertical",
  WHAT_IS_YOUR_STAFFING_MODEL: "what_is_your_staffing_model",
  HOW_MANY_LOCATIONS_DO_YOU_HAVE: "how_many_locations_do_you_have",
  WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE:
    "what_is_your_estimated_annual_revenue",
  WHAT_IS_YOUR_CURRENT_SOLUTION: "what_is_your_current_solution",

  // Stage 3: about_your_business
  WHATS_YOUR_BUSINESS_NAME: "whats_your_business_name",
  WHATS_YOUR_BUSINESS_ADDRESS: "whats_your_business_address",
  BUSINESS_ADDRESS_LINE_2: "business_address_line_2",
  BUSINESS_CITY: "business_city",
  BUSINESS_STATE: "business_state",
  BUSINESS_ZIP_CODE: "business_zip_code",
  WHATS_YOUR_BUSINESS_PHONE_NUMBER: "whats_your_business_phone_number",
  BUSINESS_PHONE_COUNTRY_CODE: "business_phone_country_code",
  BUSINESS_PHONE_AREA_CODE: "business_phone_area_code",
  BUSINESS_PHONE_NUMBER: "business_phone_number",
  BUSINESS_PHONE_NUMBER_EXT: "business_phone_number_ext",
  HOW_MANY_EMPLOYEES_DO_YOU_HAVE: "how_many_employees_do_you_have",
  WHAT_IS_YOUR_TIMEZONE: "what_is_your_timezone",

  // Stage 4: business_hours (per day)
  SUNDAY_IS_OPEN: "sunday_is_open",
  SUNDAY_OPEN_TIME: "sunday_open_time",
  SUNDAY_END_TIME: "sunday_end_time",
  MONDAY_IS_OPEN: "monday_is_open",
  MONDAY_OPEN_TIME: "monday_open_time",
  MONDAY_END_TIME: "monday_end_time",
  TUESDAY_IS_OPEN: "tuesday_is_open",
  TUESDAY_OPEN_TIME: "tuesday_open_time",
  TUESDAY_END_TIME: "tuesday_end_time",
  WEDNESDAY_IS_OPEN: "wednesday_is_open",
  WEDNESDAY_OPEN_TIME: "wednesday_open_time",
  WEDNESDAY_END_TIME: "wednesday_end_time",
  THURSDAY_IS_OPEN: "thursday_is_open",
  THURSDAY_OPEN_TIME: "thursday_open_time",
  THURSDAY_END_TIME: "thursday_end_time",
  FRIDAY_IS_OPEN: "friday_is_open",
  FRIDAY_OPEN_TIME: "friday_open_time",
  FRIDAY_END_TIME: "friday_end_time",
  SATURDAY_IS_OPEN: "saturday_is_open",
  SATURDAY_OPEN_TIME: "saturday_open_time",
  SATURDAY_END_TIME: "saturday_end_time",

  // Stage 5: business_goals
  BUSINESS_GOALS: "business_goals",
} as const;

// ==========================================
// Hardcoded Questions and Options (from screenshots)
// ==========================================

export interface QuestionConfig {
  questionKey: string;
  questionText: string;
  options?: string[];
  isMultiSelect?: boolean;
}

// Stage 1: Tell us about yourself
export const ABOUT_YOURSELF_QUESTIONS: QuestionConfig[] = [
  {
    questionKey: QUESTION_KEYS.WHATS_YOUR_TITLE,
    questionText: "What's your title?",
    options: ["Owner", "Manager", "Stylist", "Receptionist", "Other"],
    isMultiSelect: false,
  },
  {
    questionKey: QUESTION_KEYS.HOW_DID_YOU_HEAR_ABOUT_US,
    questionText: "How did you hear about us?",
    options: [
      "Google",
      "Instagram",
      "Facebook",
      "Youtube",
      "Word of Mouth",
      "Industry Event",
      "Existing Customer",
      "Other",
    ],
    isMultiSelect: true,
  },
];

// Stage 2: Some business basics
export const BUSINESS_BASICS_QUESTIONS: QuestionConfig[] = [
  {
    questionKey: QUESTION_KEYS.WHAT_IS_YOUR_BUSINESS_VERTICAL,
    questionText: "What type of business?",
    options: [
      "Hair Salon",
      "Nail Salon",
      "Barbershop",
      "Aesthetician",
      "Med Spa",
    ],
    isMultiSelect: false,
  },
  {
    questionKey: QUESTION_KEYS.WHAT_IS_YOUR_STAFFING_MODEL,
    questionText: "What is your staffing model?",
    options: ["Full-Service (W2)", "Booth Renter (1099)", "Hybrid"],
    isMultiSelect: false,
  },
  {
    questionKey: QUESTION_KEYS.HOW_MANY_LOCATIONS_DO_YOU_HAVE,
    questionText: "How many locations do you have?",
    options: ["1", "2-5", "6", "7+"],
    isMultiSelect: false,
  },
  {
    questionKey: QUESTION_KEYS.WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE,
    questionText: "What is your estimated annual revenue?",
    options: ["0-300k", "300-500k", "500-1M", "Prefer Not to Say"],
    isMultiSelect: false,
  },
  {
    questionKey: QUESTION_KEYS.WHAT_IS_YOUR_CURRENT_SOLUTION,
    questionText: "What is your current solution?",
    options: ["Vagaro", "Booksy", "GlossGenius", "Pen & Paper", "Other"],
    isMultiSelect: false,
  },
];

// Stage 3: About your business (text inputs + employee count chips)
export const ABOUT_YOUR_BUSINESS_QUESTIONS: QuestionConfig[] = [
  {
    questionKey: QUESTION_KEYS.WHATS_YOUR_BUSINESS_NAME,
    questionText: "What's your business name?",
  },
  {
    questionKey: QUESTION_KEYS.WHATS_YOUR_BUSINESS_ADDRESS,
    questionText: "What's your business address?",
  },
  {
    questionKey: QUESTION_KEYS.WHATS_YOUR_BUSINESS_PHONE_NUMBER,
    questionText: "What's your business phone number?",
  },
  {
    questionKey: QUESTION_KEYS.HOW_MANY_EMPLOYEES_DO_YOU_HAVE,
    questionText: "How many employees do you have?",
    options: ["Just me", "2-5", "6-10", "11-20", "20+"],
    isMultiSelect: false,
  },
];

// Stage 5: Business goals
export interface GoalOptionConfig {
  value: string;
  title: string;
  description: string;
  icon: string;
}

export const BUSINESS_GOALS_OPTIONS: GoalOptionConfig[] = [
  {
    value: "build_a_website",
    title: "Build a website",
    description: "Create a branded site to attract new clients",
    icon: "IconBrowser",
  },
  {
    value: "get_discovered_more_online",
    title: "Get discovered more online",
    description: "Improve your Google ranking and online presence",
    icon: "IconWorld",
  },
  {
    value: "grow_your_business_and_attract_new_clients",
    title: "Grow your business & attract new clients",
    description:
      "Marketing, referrals, & promotions that help bring in new customers",
    icon: "IconSpeakerphone",
  },
  {
    value: "accept_online_bookings",
    title: "Accept online bookings",
    description: "Make it easy for clients to schedule services online",
    icon: "IconLink",
  },
  {
    value: "sell_products_online_or_in_store",
    title: "Sell products online or in-store",
    description: "Set up product catalog + checkout",
    icon: "IconShoppingBag",
  },
  {
    value: "accept_online_and_or_in_store_payments",
    title: "Accept online and/or in-store payments",
    description: "Securely process payments anywhere",
    icon: "IconCreditCard",
  },
  {
    value: "accept_deposits",
    title: "Accept deposits",
    description: "Reduce no-shows with deposits",
    icon: "IconCoins",
  },
  {
    value: "im_not_sure_yet",
    title: "I'm not sure yet",
    description: "Explore the platform and learn the essentials",
    icon: "IconClipboard",
  },
];

// ==========================================
// Step Configuration
// ==========================================

export interface StepConfig {
  stage: OnboardingStage;
  title: string;
  subtitle: string;
  showSkip?: boolean;
}

export const STEP_CONFIGS: Record<OnboardingStage, StepConfig> = {
  about_yourself: {
    stage: "about_yourself",
    title: "Tell us about yourself",
    subtitle: "We'd love to learn a little bit about you.",
  },
  business_basics: {
    stage: "business_basics",
    title: "Some business basics",
    subtitle: "A few quick details so we understand your business structure.",
  },
  about_your_business: {
    stage: "about_your_business",
    title: "About your business",
    subtitle: "This helps us set up your booking tools and website.",
  },
  business_hours: {
    stage: "business_hours",
    title: "Business hours",
    subtitle:
      "Set the days and times clients can book with you. Add your breaks later.",
  },
  business_goals: {
    stage: "business_goals",
    title: "Business goals",
    subtitle: "This helps us set up your booking tools and website.",
  },
};

// ==========================================
// Form Data Types
// ==========================================

// Stage 1: About Yourself
export interface AboutYourselfData {
  title: string | null;
  referralSources: string[];
}

// Stage 2: Business Basics
export interface BusinessBasicsData {
  businessType: string | null;
  businessVertical: string | null;
  staffingModel: string | null;
  locationCount: string | null;
  revenueRange: string | null;
  currentSolution: string | null;
}

// Stage 3: About Your Business
export interface AboutYourBusinessData {
  businessName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  countryCode: string;
  areaCode: string;
  phoneNumber: string;
  phoneNumberExt: string;
  employeeCount: string | null;
  timezone: string;
}

// Stage 4: Business Hours
export interface DayHours {
  isOpen: boolean;
  startTime: string; // Format: "HH:MM" (24-hour)
  endTime: string; // Format: "HH:MM" (24-hour)
}

export interface BusinessHoursData {
  sunday: DayHours;
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
}

// Stage 5: Business Goals
export interface BusinessGoalsData {
  selectedGoals: string[];
}

// Combined Onboarding Data
export interface OnboardingData {
  stage: OnboardingStage;
  aboutYourself: AboutYourselfData;
  businessBasics: BusinessBasicsData;
  aboutYourBusiness: AboutYourBusinessData;
  businessHours: BusinessHoursData;
  businessGoals: BusinessGoalsData;
}

// ==========================================
// API Types
// ==========================================

export type OnboardingState = "null" | "inprogress" | "complete" | null;
export type OnboardingSection =
  | "initial_onboarding"
  | "progressive_employees"
  | "progressive_services"
  | "progressive_payments";

// Progressive status type (alias for OnboardingState for clarity in progressive onboarding context)
export type ProgressiveStatus = OnboardingState;

// GET /onboardings response
// Note: API returns camelCase field names
export interface OnboardingProgressResponse {
  id: string;
  staffId: string;
  salonId: string | null;
  // Initial onboarding status and stage
  initialOnboarding: ProgressiveStatus;
  initialOnboardingStage: OnboardingStage | null;
  // Progressive onboarding status fields
  progressiveEmployees: ProgressiveStatus;
  progressiveServices: ProgressiveStatus;
  progressivePayments: ProgressiveStatus;
  // Timestamps
  createdDt: string;
  updatedDt: string;
}

// Helper to check if all progressive onboarding steps are complete
export function isProgressiveOnboardingComplete(
  progress: OnboardingProgressResponse,
): boolean {
  return (
    progress.progressiveEmployees === "complete" &&
    progress.progressiveServices === "complete" &&
    progress.progressivePayments === "complete"
  );
}

// Helper to check if user needs to go to onboarding dashboard
export function shouldShowOnboardingDashboard(
  progress: OnboardingProgressResponse,
): boolean {
  return (
    progress.initialOnboarding === "complete" &&
    !isProgressiveOnboardingComplete(progress)
  );
}

// Helper to check if user is still in initial onboarding flow
export function isInInitialOnboarding(
  progress: OnboardingProgressResponse,
): boolean {
  return (
    progress.initialOnboarding === "inprogress" ||
    progress.initialOnboarding === null ||
    progress.initialOnboarding === "null"
  );
}

// PUT /onboardings request
// NOTE: the backend associates progress with a salon via the ?salonId= QUERY
// param only — a body salonId is ignored. Pass it as the second argument to
// updateOnboardingProgress instead, and don't fire the save at all when no
// salon id is available (the backend would silently fall back to a
// staff-keyed row that can belong to another location).
export interface UpdateOnboardingProgressRequest {
  section: OnboardingSection;
  state: OnboardingState;
  stage?: OnboardingStage;
}

// Answer types for the API
export type OnboardingAnswerType = "SINGLE" | "MULTI" | "TEXT";

// PUT /onboardings/initial_onboarding/answers request
export interface UpsertOnboardingAnswerRequest {
  questionKey: string;
  questionValue: string;
  answerValue: string;
  answerType: OnboardingAnswerType;
  stage: OnboardingStage;
}

// PUT /onboardings/initial_onboarding/answers response
export interface UpsertOnboardingAnswerResponse {
  success: boolean;
  answer: {
    id: number;
    staff_id: string;
    salon_id: number | null;
    question_key: string;
    answer_value: string;
  };
}

// GET /onboardings/initial_onboarding/answers response
// Returns saved answers with their metadata
export interface OnboardingSavedAnswer {
  id: number;
  salonId: number | null;
  staffId: string;
  questionKey: string;
  questionValue: string;
  answerValue: string;
  answerType: OnboardingAnswerType;
  stage: OnboardingStage;
  createdDt: string;
  updatedDt: string;
}

export type GetOnboardingQuestionsResponse = OnboardingSavedAnswer[];

// Create salon request (for stage 3)
export interface CreateSalonRequest {
  salon_name: string;
  street_address_1: string;
  street_address_2?: string;
  city: string;
  state: string;
  zip_code: string;
  country_code?: string;
  area_code?: string;
  phone_number?: string;
  phone_number_ext?: string;
  timezone: string;
  business_model?: string;
  business_vertical?: string;
  staffing_model?: string;
}

// Update salon request (for stage 4 - business hours)
export interface UpdateSalonHoursRequest {
  id: string;
  sunday_is_open?: boolean;
  sunday_open_time?: string;
  sunday_end_time?: string;
  monday_is_open?: boolean;
  monday_open_time?: string;
  monday_end_time?: string;
  tuesday_is_open?: boolean;
  tuesday_open_time?: string;
  tuesday_end_time?: string;
  wednesday_is_open?: boolean;
  wednesday_open_time?: string;
  wednesday_end_time?: string;
  thursday_is_open?: boolean;
  thursday_open_time?: string;
  thursday_end_time?: string;
  friday_is_open?: boolean;
  friday_open_time?: string;
  friday_end_time?: string;
  saturday_is_open?: boolean;
  saturday_open_time?: string;
  saturday_end_time?: string;
}

// ==========================================
// Helper Functions
// ==========================================

/**
 * Convert 24-hour time format (HH:MM) to 12-hour format with AM/PM (HH:MM AM/PM)
 * Backend expects 12-hour format: "09:00 AM", "05:00 PM", "11:00 PM"
 */
function convertTo12HourFormat(
  time24: string | null | undefined,
): string | undefined {
  if (!time24) return undefined;

  const [hourStr, minute] = time24.split(":");
  const hour = parseInt(hourStr, 10);

  if (isNaN(hour)) return undefined;

  const ampm = hour < 12 ? "AM" : "PM";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const formattedHour = hour12.toString().padStart(2, "0");

  return `${formattedHour}:${minute} ${ampm}`;
}

/**
 * Convert 12-hour time format (HH:MM AM/PM) to 24-hour format (HH:MM)
 * Used when loading data from the API
 */
export function convertTo24HourFormat(time12: string): string {
  if (!time12) return "";

  // If already in 24-hour format (no AM/PM), return as-is
  if (!time12.includes("AM") && !time12.includes("PM")) {
    return time12;
  }

  const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12;

  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const period = match[3].toUpperCase();

  if (period === "AM" && hour === 12) {
    hour = 0;
  } else if (period === "PM" && hour !== 12) {
    hour += 12;
  }

  return `${hour.toString().padStart(2, "0")}:${minute}`;
}

// Shared helper: convert DayHours to 12-hour API times with default end time fallback
// Returns undefined for times when day is closed or startTime is empty;
// guarantees end_time is set whenever start_time is set.
function formatDayTimes(dayData: DayHours): {
  start_time: string | null;
  end_time: string | null;
} {
  if (!dayData.isOpen || !dayData.startTime || !dayData.startTime.trim()) {
    return { start_time: null, end_time: null };
  }

  const startTime = convertTo12HourFormat(dayData.startTime);
  if (!startTime) {
    return { start_time: null, end_time: null };
  }

  // If start_time is set, end_time must also be set — default to 5 PM
  const endTimeToUse =
    dayData.endTime && dayData.endTime.trim() ? dayData.endTime : "17:00";
  const endTime = convertTo12HourFormat(endTimeToUse) ?? null;

  return { start_time: startTime, end_time: endTime };
}

// Helper to run formatDayTimes over all 7 days
function formatAllDays(hours: BusinessHoursData) {
  return {
    sunday: formatDayTimes(hours.sunday),
    monday: formatDayTimes(hours.monday),
    tuesday: formatDayTimes(hours.tuesday),
    wednesday: formatDayTimes(hours.wednesday),
    thursday: formatDayTimes(hours.thursday),
    friday: formatDayTimes(hours.friday),
    saturday: formatDayTimes(hours.saturday),
  };
}

// Helper to convert BusinessHoursData to API format
// Converts 24-hour times to 12-hour AM/PM format as expected by the backend
export function businessHoursToApiFormat(
  salonId: string,
  hours: BusinessHoursData,
): UpdateSalonHoursRequest {
  const days = formatAllDays(hours);

  return {
    id: salonId,
    sunday_is_open: hours.sunday.isOpen,
    sunday_open_time: days.sunday.start_time ?? undefined,
    sunday_end_time: days.sunday.end_time ?? undefined,
    monday_is_open: hours.monday.isOpen,
    monday_open_time: days.monday.start_time ?? undefined,
    monday_end_time: days.monday.end_time ?? undefined,
    tuesday_is_open: hours.tuesday.isOpen,
    tuesday_open_time: days.tuesday.start_time ?? undefined,
    tuesday_end_time: days.tuesday.end_time ?? undefined,
    wednesday_is_open: hours.wednesday.isOpen,
    wednesday_open_time: days.wednesday.start_time ?? undefined,
    wednesday_end_time: days.wednesday.end_time ?? undefined,
    thursday_is_open: hours.thursday.isOpen,
    thursday_open_time: days.thursday.start_time ?? undefined,
    thursday_end_time: days.thursday.end_time ?? undefined,
    friday_is_open: hours.friday.isOpen,
    friday_open_time: days.friday.start_time ?? undefined,
    friday_end_time: days.friday.end_time ?? undefined,
    saturday_is_open: hours.saturday.isOpen,
    saturday_open_time: days.saturday.start_time ?? undefined,
    saturday_end_time: days.saturday.end_time ?? undefined,
  };
}

// Staff working hours update request (for back-populating owner hours)
// Null values explicitly clear times for closed days on the backend
export interface UpdateStaffHoursRequest {
  id: string;
  sunday_start_time: string | null;
  sunday_end_time: string | null;
  monday_start_time: string | null;
  monday_end_time: string | null;
  tuesday_start_time: string | null;
  tuesday_end_time: string | null;
  wednesday_start_time: string | null;
  wednesday_end_time: string | null;
  thursday_start_time: string | null;
  thursday_end_time: string | null;
  friday_start_time: string | null;
  friday_end_time: string | null;
  saturday_start_time: string | null;
  saturday_end_time: string | null;
}

// Helper to convert BusinessHoursData to staff working hours format
// Used during onboarding to back-populate salon hours into the owner's working hours
// Sends null for closed days so the backend clears any existing times
export function businessHoursToStaffFormat(
  staffId: string,
  hours: BusinessHoursData,
): UpdateStaffHoursRequest {
  const days = formatAllDays(hours);

  return {
    id: staffId,
    sunday_start_time: days.sunday.start_time,
    sunday_end_time: days.sunday.end_time,
    monday_start_time: days.monday.start_time,
    monday_end_time: days.monday.end_time,
    tuesday_start_time: days.tuesday.start_time,
    tuesday_end_time: days.tuesday.end_time,
    wednesday_start_time: days.wednesday.start_time,
    wednesday_end_time: days.wednesday.end_time,
    thursday_start_time: days.thursday.start_time,
    thursday_end_time: days.thursday.end_time,
    friday_start_time: days.friday.start_time,
    friday_end_time: days.friday.end_time,
    saturday_start_time: days.saturday.start_time,
    saturday_end_time: days.saturday.end_time,
  };
}

// Helper to convert AboutYourBusinessData to CreateSalonRequest
export function aboutYourBusinessToCreateSalonRequest(
  data: AboutYourBusinessData,
  businessType?: string,
  businessVertical?: string,
  staffingModel?: string,
): CreateSalonRequest {
  // Use timezone from data, or default to browser's timezone if not provided
  const resolvedTimezone =
    data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const resolvedVertical =
    businessVertical != null && businessVertical.trim() !== ""
      ? (normalizeBusinessVerticalValue(businessVertical) ?? businessVertical)
      : undefined;

  const request: CreateSalonRequest = {
    salon_name: data.businessName,
    street_address_1: data.addressLine1,
    street_address_2: data.addressLine2 || undefined,
    city: data.city,
    state: data.state,
    zip_code: data.zipCode,
    country_code: "1", // Always assume country code is 1 for business
    area_code: data.areaCode || undefined,
    phone_number: data.phoneNumber || undefined,
    // phone_number_ext is not included - set to undefined (not sent to backend)
    timezone: resolvedTimezone,
    business_model: businessType,
    business_vertical: resolvedVertical,
  };

  if (staffingModel) {
    request.staffing_model = staffingModel;
  }

  return request;
}

// ==========================================
// Answer Type Helpers
// ==========================================

// Map of question keys to their answer types
// SINGLE: selected from predefined options (single selection)
// MULTI: selected from predefined options (multi selection)
// TEXT: free-text input (business info, times, etc.)
const SINGLE_SELECT_QUESTIONS: Set<string> = new Set([
  QUESTION_KEYS.WHATS_YOUR_TITLE,
  QUESTION_KEYS.WHAT_TYPE_OF_BUSINESS,
  QUESTION_KEYS.WHAT_IS_YOUR_BUSINESS_VERTICAL,
  QUESTION_KEYS.WHAT_IS_YOUR_STAFFING_MODEL,
  QUESTION_KEYS.HOW_MANY_LOCATIONS_DO_YOU_HAVE,
  QUESTION_KEYS.WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE,
  QUESTION_KEYS.WHAT_IS_YOUR_CURRENT_SOLUTION,
  QUESTION_KEYS.HOW_MANY_EMPLOYEES_DO_YOU_HAVE,
  QUESTION_KEYS.WHAT_IS_YOUR_TIMEZONE,
  // Business hours isOpen fields are single select (true/false)
  QUESTION_KEYS.SUNDAY_IS_OPEN,
  QUESTION_KEYS.MONDAY_IS_OPEN,
  QUESTION_KEYS.TUESDAY_IS_OPEN,
  QUESTION_KEYS.WEDNESDAY_IS_OPEN,
  QUESTION_KEYS.THURSDAY_IS_OPEN,
  QUESTION_KEYS.FRIDAY_IS_OPEN,
  QUESTION_KEYS.SATURDAY_IS_OPEN,
]);

const MULTI_SELECT_QUESTIONS: Set<string> = new Set([
  QUESTION_KEYS.HOW_DID_YOU_HEAR_ABOUT_US,
  QUESTION_KEYS.BUSINESS_GOALS,
]);

// Helper to determine the correct answerType for a question
export function getAnswerTypeForQuestion(
  questionKey: string,
): OnboardingAnswerType {
  if (SINGLE_SELECT_QUESTIONS.has(questionKey)) {
    return "SINGLE";
  }
  if (MULTI_SELECT_QUESTIONS.has(questionKey)) {
    return "MULTI";
  }
  // Default to TEXT for free-text inputs (business name, address, times, etc.)
  return "TEXT";
}

// Map question keys to their stages
const QUESTION_KEY_TO_STAGE: Record<string, OnboardingStage> = {
  // Stage 1: about_yourself
  [QUESTION_KEYS.WHATS_YOUR_TITLE]: "about_yourself",
  [QUESTION_KEYS.HOW_DID_YOU_HEAR_ABOUT_US]: "about_yourself",

  // Stage 2: business_basics
  [QUESTION_KEYS.WHAT_TYPE_OF_BUSINESS]: "business_basics",
  [QUESTION_KEYS.WHAT_IS_YOUR_BUSINESS_VERTICAL]: "business_basics",
  [QUESTION_KEYS.WHAT_IS_YOUR_STAFFING_MODEL]: "business_basics",
  [QUESTION_KEYS.HOW_MANY_LOCATIONS_DO_YOU_HAVE]: "business_basics",
  [QUESTION_KEYS.WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE]: "business_basics",
  [QUESTION_KEYS.WHAT_IS_YOUR_CURRENT_SOLUTION]: "business_basics",

  // Stage 3: about_your_business
  [QUESTION_KEYS.WHATS_YOUR_BUSINESS_NAME]: "about_your_business",
  [QUESTION_KEYS.WHATS_YOUR_BUSINESS_ADDRESS]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_ADDRESS_LINE_2]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_CITY]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_STATE]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_ZIP_CODE]: "about_your_business",
  [QUESTION_KEYS.WHATS_YOUR_BUSINESS_PHONE_NUMBER]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_PHONE_COUNTRY_CODE]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_PHONE_AREA_CODE]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_PHONE_NUMBER]: "about_your_business",
  [QUESTION_KEYS.BUSINESS_PHONE_NUMBER_EXT]: "about_your_business",
  [QUESTION_KEYS.WHAT_IS_YOUR_TIMEZONE]: "about_your_business",
  [QUESTION_KEYS.HOW_MANY_EMPLOYEES_DO_YOU_HAVE]: "about_your_business",

  // Stage 4: business_hours
  [QUESTION_KEYS.SUNDAY_IS_OPEN]: "business_hours",
  [QUESTION_KEYS.SUNDAY_OPEN_TIME]: "business_hours",
  [QUESTION_KEYS.SUNDAY_END_TIME]: "business_hours",
  [QUESTION_KEYS.MONDAY_IS_OPEN]: "business_hours",
  [QUESTION_KEYS.MONDAY_OPEN_TIME]: "business_hours",
  [QUESTION_KEYS.MONDAY_END_TIME]: "business_hours",
  [QUESTION_KEYS.TUESDAY_IS_OPEN]: "business_hours",
  [QUESTION_KEYS.TUESDAY_OPEN_TIME]: "business_hours",
  [QUESTION_KEYS.TUESDAY_END_TIME]: "business_hours",
  [QUESTION_KEYS.WEDNESDAY_IS_OPEN]: "business_hours",
  [QUESTION_KEYS.WEDNESDAY_OPEN_TIME]: "business_hours",
  [QUESTION_KEYS.WEDNESDAY_END_TIME]: "business_hours",
  [QUESTION_KEYS.THURSDAY_IS_OPEN]: "business_hours",
  [QUESTION_KEYS.THURSDAY_OPEN_TIME]: "business_hours",
  [QUESTION_KEYS.THURSDAY_END_TIME]: "business_hours",
  [QUESTION_KEYS.FRIDAY_IS_OPEN]: "business_hours",
  [QUESTION_KEYS.FRIDAY_OPEN_TIME]: "business_hours",
  [QUESTION_KEYS.FRIDAY_END_TIME]: "business_hours",
  [QUESTION_KEYS.SATURDAY_IS_OPEN]: "business_hours",
  [QUESTION_KEYS.SATURDAY_OPEN_TIME]: "business_hours",
  [QUESTION_KEYS.SATURDAY_END_TIME]: "business_hours",

  // Stage 5: business_goals
  [QUESTION_KEYS.BUSINESS_GOALS]: "business_goals",
};

// Helper to get the stage for a question key
export function getStageForQuestion(questionKey: string): OnboardingStage {
  return QUESTION_KEY_TO_STAGE[questionKey] || "about_yourself";
}

// ==========================================
// Validation Functions for Required Fields
// ==========================================

/**
 * Validates required fields for the "About Your Business" stage
 * Required: businessName, addressLine1, city, state, zipCode, countryCode, areaCode, phoneNumber, timezone
 */
export function validateAboutYourBusiness(
  data: AboutYourBusinessData,
): boolean {
  return (
    !!data.businessName?.trim() &&
    !!data.addressLine1?.trim() &&
    !!data.city?.trim() &&
    !!data.state?.trim() &&
    !!data.zipCode?.trim() &&
    !!data.countryCode?.trim() &&
    !!data.areaCode?.trim() &&
    data.areaCode.length === 3 &&
    !!data.phoneNumber?.trim() &&
    data.phoneNumber.length === 7 &&
    !!data.timezone?.trim() &&
    !!data.employeeCount?.trim()
  );
}

/**
 * Validates required fields for the "About Yourself" stage
 * Required: title, referralSources
 */
export function validateAboutYourself(data: AboutYourselfData): boolean {
  return (
    !!data.title &&
    data.title.trim() !== "" &&
    Array.isArray(data.referralSources) &&
    data.referralSources.length > 0
  );
}

/**
 * Validates required fields for the "Business Basics" stage
 * Required: locationCount, revenueRange, currentSolution
 * Note: businessVertical and staffingModel moved to "About Your Business" stage.
 */
export function validateBusinessBasics(data: BusinessBasicsData): boolean {
  return (
    !!data.locationCount &&
    data.locationCount.trim() !== "" &&
    !!data.revenueRange &&
    data.revenueRange.trim() !== "" &&
    !!data.currentSolution &&
    data.currentSolution.trim() !== ""
  );
}

/**
 * Validates that business vertical and staffing model are set.
 * These fields live on BusinessBasicsData but are rendered on the
 * "About Your Business" stage, so they are validated there.
 */
export function validateVerticalAndStaffing(data: BusinessBasicsData): boolean {
  const hasVertical =
    normalizeBusinessVerticalValue(data.businessVertical) != null;
  const hasStaffingModel =
    !!data.staffingModel && data.staffingModel.trim() !== "";
  return hasVertical && hasStaffingModel;
}

/**
 * Validates required fields for the "Business Goals" stage
 * Required: at least one goal selected
 */
export function validateBusinessGoals(data: BusinessGoalsData): boolean {
  return Array.isArray(data.selectedGoals) && data.selectedGoals.length > 0;
}

/**
 * Validates required fields for the "Business Hours" stage
 * Required: At least one day must be open with both startTime and endTime
 */
export function validateBusinessHours(data: BusinessHoursData): boolean {
  const days: (keyof BusinessHoursData)[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  // At least one day must be open
  const hasOpenDay = days.some((day) => data[day].isOpen);

  if (!hasOpenDay) return false;

  // All open days must have both start and end times
  return days.every((day) => {
    const dayData = data[day];
    if (dayData.isOpen) {
      return (
        !!dayData.startTime?.trim() &&
        !!dayData.endTime?.trim() &&
        dayData.startTime.length === 5 &&
        dayData.endTime.length === 5
      );
    }
    return true; // Closed days are valid
  });
}

/**
 * Validates if a stage has all required fields filled
 */
export function isStageValid(
  stage: OnboardingStage,
  data: OnboardingData,
): boolean {
  switch (stage) {
    case "about_yourself":
      return validateAboutYourself(data.aboutYourself);
    case "business_basics":
      return validateBusinessBasics(data.businessBasics);
    case "about_your_business":
      return (
        validateAboutYourBusiness(data.aboutYourBusiness) &&
        validateVerticalAndStaffing(data.businessBasics)
      );
    case "business_hours":
      return validateBusinessHours(data.businessHours);
    case "business_goals":
      return validateBusinessGoals(data.businessGoals);
    default:
      return true;
  }
}

// Helper to create empty initial state
export function createEmptyOnboardingData(): OnboardingData {
  const emptyDayHours: DayHours = {
    isOpen: false,
    startTime: "",
    endTime: "",
  };

  return {
    stage: "about_yourself",
    aboutYourself: {
      title: null,
      referralSources: [],
    },
    businessBasics: {
      businessType: null,
      businessVertical: null,
      staffingModel: null,
      locationCount: null,
      revenueRange: null,
      currentSolution: null,
    },
    aboutYourBusiness: {
      businessName: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      countryCode: "1",
      areaCode: "",
      phoneNumber: "",
      phoneNumberExt: "",
      employeeCount: null,
      timezone: "", // Start empty - will be set by component if browser timezone matches a US timezone
    },
    businessHours: {
      sunday: { ...emptyDayHours },
      monday: { ...emptyDayHours },
      tuesday: { ...emptyDayHours },
      wednesday: { ...emptyDayHours },
      thursday: { ...emptyDayHours },
      friday: { ...emptyDayHours },
      saturday: { ...emptyDayHours },
    },
    businessGoals: {
      selectedGoals: [],
    },
  };
}
